import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface FooterLink {
  label: string;
  to?: string;       // internal route (react-router)
  href?: string;     // external / mailto / hash
  external?: boolean;
}

interface FooterCol {
  titleKey: 'platform' | 'features' | 'company';
  links: FooterLink[];
}

const FOOTER_COLS: FooterCol[] = [
  {
    titleKey: 'platform',
    links: [
      { label: 'For Patients',     to: '/register?role=patient' },
      { label: 'For Doctors',      to: '/register?role=doctor' },
      { label: 'For Nurses',       to: '/register?role=nurse' },
      { label: 'For Pharmacies',   to: '/register?role=pharmacist' },
      { label: 'For Hospitals',    to: '/register?role=facility&type=hospital' },
      { label: 'For Laboratories', to: '/register?role=lab_tech' },
    ],
  },
  {
    titleKey: 'features',
    links: [
      { label: 'Virtual Consultations', href: '/#features' },
      { label: 'Heka AI',               href: '/#ai' },
      { label: 'NHIS Billing',          to:   '/legal/nhis-compliance' },
      { label: 'Pharmacy Delivery',     href: '/#features-grid' },
      { label: 'Home Nursing',          href: '/#providers' },
      { label: 'Health Records',        href: '/#features-grid' },
    ],
  },
  {
    titleKey: 'company',
    links: [
      { label: 'About Heka', href: '/#nhis' },
      { label: 'Careers',    href: 'mailto:careers@heka.care' },
      { label: 'Blog',       href: 'https://blog.heka.care', external: true },
      { label: 'Press',      href: 'mailto:press@heka.care' },
      { label: 'Contact',    href: 'mailto:hello@heka.care' },
      { label: 'Support',    href: 'mailto:support@heka.care' },
    ],
  },
];

const LEGAL_LINKS = [
  { to: '/legal/privacy',           label: 'Privacy Policy' },
  { to: '/legal/terms',             label: 'Terms of Service' },
  { to: '/legal/cookies',           label: 'Cookie Policy' },
  { to: '/legal/nhis-compliance',   label: 'NHIS Compliance' },
];

const SOCIALS = [
  { label: 'X',        aria: 'Follow Heka.care on X',        url: 'https://x.com/hekacare' },
  { label: 'in',       aria: 'Heka.care on LinkedIn',        url: 'https://www.linkedin.com/company/heka-care' },
  { label: 'f',        aria: 'Heka.care on Facebook',        url: 'https://facebook.com/hekacare' },
  { label: '\u25B6',   aria: 'Heka.care on YouTube',         url: 'https://youtube.com/@hekacare' },
];

function FooterLinkItem({ link }: { link: FooterLink }) {
  if (link.to) {
    return <Link to={link.to}>{link.label}</Link>;
  }
  if (link.external || /^https?:\/\//.test(link.href ?? '')) {
    return (
      <a href={link.href} target="_blank" rel="noreferrer noopener">
        {link.label}
      </a>
    );
  }
  return <a href={link.href ?? '#'}>{link.label}</a>;
}

export default function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <footer className="landing-footer">
      <div className="footer-top">
        <div className="footer-brand">
          <Link to="/" aria-label="Heka.care home" style={{ display: 'inline-block', marginBottom: 12 }}>
            <img
              src="/logo.png"
              srcSet="/logo-small.png 240w, /logo-email.png 480w, /logo.png 1160w"
              sizes="260px"
              alt="Heka.care — Telehealth for Ghana"
              width="260"
              height="61"
              style={{ height: 48, width: 'auto', display: 'block', filter: 'brightness(0) invert(1)' }}
              decoding="async"
            />
          </Link>
          <p className="footer-tagline">
            {t('footer.tagline')}
          </p>
          <div className="footer-socials">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                className="social-btn"
                href={s.url}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={s.aria}
              >
                {s.label}
              </a>
            ))}
          </div>
        </div>

        {FOOTER_COLS.map((col) => (
          <div className="footer-col" key={col.titleKey}>
            <h4>{t(`footer.columns.${col.titleKey}`)}</h4>
            <ul>
              {col.links.map((link) => (
                <li key={link.label}>
                  <FooterLinkItem link={link} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="footer-bottom">
        <p>&copy; {year} Heka.care &middot; Accra, Ghana &middot; {t('footer.rights')}</p>
        <div className="footer-legal">
          {LEGAL_LINKS.map((l) => (
            <Link key={l.to} to={l.to}>
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </footer>
  );
}
